import { useQuery } from '@tanstack/react-query';
import { getStaticTradePairs } from '@ui/constants/tokens/tradePairs';
import { useClientDataChainId } from '@ui/hooks/datachain';
import { usePriceMapLatest } from '@ui/hooks/price/usePriceLatest';
import { ZERO_BIG_NUMBER } from '@ui/lib/bignumber';
import { queryClient } from '@ui/lib/reactQuery';
import { useBackendPairsAll } from '@ui/services/backend/hooks/getPairs/useBackendPairsAll';
import { usePairsV4 } from '@ui/services/contract/hooks/PairsManagerFacet/usePairsV4';
import { getMarketInfosQuery } from '@ui/services/contract/hooks/TradingReaderFacet/useGetMarketInfos/query';
import { Address } from '@ui/types/alias';
import { TradePair } from '@ui/types/tradePair';
import { getTradePairsVolume } from '@ui/utils/getTradePairsVolume';
import { objectFromEntries } from '@ui/utils/objectFromEntries';
import { getDisplayFundingFeeRate } from '@ui/utils/trade/getDisplayFundingFeeRate';
import { unifyTradePairs } from '@ui/utils/unifyTradePairs';
import BigNumber from 'bignumber.js';
import { useMemo } from 'react';
import { usePublicClient } from 'wagmi';

/**
 * Aggregate data from 3 sources
 * - FE config
 * - SC config
 * - SC market info
 */
export const useTradePairInfos = () => {
  const chainId = useClientDataChainId();
  const client = usePublicClient({
    chainId,
  });

  const initialData = useMemo(() => {
    return getStaticTradePairs(chainId);
  }, [chainId]);
  const { data } = useQuery({
    initialData,
    refetchOnWindowFocus: false,
    queryKey: ['useTradePairInfos', chainId, client?.uid],
    queryFn: async (): Promise<TradePair[]> => {
      const remoteList = Object.values(
        await queryClient.ensureQueryData({
          queryKey: useBackendPairsAll.getQueryKey(chainId),
          queryFn: useBackendPairsAll.getQueryFn(chainId),
        }),
      );

      const pairBases = remoteList.map((v) => v.address);

      const marketInfo = await getMarketInfosQuery.getQueryFn({
        chainId,
        pairBases: pairBases,
        client,
      })();

      const marketInfoMap = objectFromEntries(
        marketInfo.map((v) => [v.pairBase.toLowerCase(), v]),
      );

      const pairInfo = await usePairsV4.getFn({
        chainId,
        client,
      })();
      const pairInfoMap = objectFromEntries(
        pairInfo.map((v) => [v.base.toLowerCase(), v]),
      );

      return remoteList.map((v) => {
        const displayFundingFeeRate = getDisplayFundingFeeRate({
          basicFundingRatePerSecond:
            marketInfoMap[v.address.toLowerCase()]?.fundingFeeRate,
          longHoldingFeeRatePerSecond:
            pairInfoMap[v.address.toLowerCase()]?.longHoldingFeeRate,
          shortHoldingFeeRatePerSecond:
            pairInfoMap[v.address.toLowerCase()]?.shortHoldingFeeRate,
        });

        return {
          ...v,
          ...marketInfoMap[v.address.toLowerCase()],
          ...pairInfoMap[v.address.toLowerCase()],
          displayLongFundingFeeRatePerSecond:
            displayFundingFeeRate?.displayLongFundingFeeRatePerSecond,
          displayShortFundingFeeRatePerSecond:
            displayFundingFeeRate?.displayShortFundingFeeRatePerSecond,
        };
      });
    },
  });

  return data;
};

export const useUniqueSortedTradePairInfos = (): TradePair[] | undefined => {
  const pairs = useTradePairInfos();
  const { data: latestPrices } = usePriceMapLatest();

  return useMemo(() => {
    if (!pairs) return undefined;
    const pairToVolume = getTradePairsVolume(pairs);
    const pairToOpenInterestUsd = getTradePairsOpenInterestUsd(
      pairs,
      latestPrices,
    );

    const sortedPairs = [...pairs].sort((a, b) => {
      const volumeCompare = compareBigNumberDesc(
        pairToVolume.get(a.baseName),
        pairToVolume.get(b.baseName),
      );
      if (volumeCompare !== 0) return volumeCompare;

      const openInterestCompare = compareBigNumberDesc(
        pairToOpenInterestUsd.get(a.baseName),
        pairToOpenInterestUsd.get(b.baseName),
      );
      if (openInterestCompare !== 0) return openInterestCompare;

      return a.baseName.toLowerCase().localeCompare(b.baseName.toLowerCase());
    });

    return unifyTradePairs(sortedPairs);
  }, [latestPrices, pairs]);
};

const compareBigNumberDesc = (
  a: BigNumber | undefined,
  b: BigNumber | undefined,
) => {
  return (b ?? ZERO_BIG_NUMBER).comparedTo(a ?? ZERO_BIG_NUMBER);
};

const getTradePairsOpenInterestUsd = (
  tradePairs: readonly TradePair[],
  latestPrices: ReturnType<typeof usePriceMapLatest>['data'],
) => {
  const pairToOpenInterestUsd = new Map<TradePair['baseName'], BigNumber>();

  tradePairs.forEach((tradePair) => {
    const price =
      latestPrices?.[tradePair.baseSymbol as keyof typeof latestPrices] ??
      ZERO_BIG_NUMBER;
    const openInterestUsd = tradePair.longQty
      .plus(tradePair.shortQty)
      .multipliedBy(price);

    pairToOpenInterestUsd.set(
      tradePair.baseName,
      openInterestUsd.plus(
        pairToOpenInterestUsd.get(tradePair.baseName) ?? ZERO_BIG_NUMBER,
      ),
    );
  });

  return pairToOpenInterestUsd;
};

export const useTradePairInfosByPairBase = (tradePairBase: Address) => {
  const pairs = useTradePairInfos();

  return useMemo(() => {
    return pairs?.find(
      (p) => p.address.toLowerCase() === tradePairBase.toLowerCase(),
    );
  }, [pairs, tradePairBase]);
};

export const useTradePairInfosByPairBaseSymbol = (
  tradePairBaseSymbol: TradePair['baseSymbol'],
) => {
  const pairs = useTradePairInfos();

  return useMemo(
    () =>
      pairs?.filter(
        (p) => p.baseSymbol.toLowerCase() === tradePairBaseSymbol.toLowerCase(),
      ),
    [pairs, tradePairBaseSymbol],
  );
};
